<template>
  <div>
    <error-box v-if="error" @reload="load()" />

    <loader v-else-if="loading" />

    <template v-if="!error && !loading">
      <panel :title="$tc('tournament.general_info')">
        <div class="name-row">
          <main-select
            v-model="form.isCamp"
            :label="$tc('general.type')"
            :options="typeOptions"
            :placeholder="$tc('seminar.type_placeholder')"
            clearable
            class="type-select"
          />
          <main-input
            v-model="form.name"
            :label="$tc('form.name')"
            :validator="$v.form.name"
          />
        </div>
        <main-textarea
          v-model="form.description"
          :label="$tc('form.description')"
          :validator="$v.form.description"
        />
        <el-row :gutter="16">
          <el-col :span="12">
            <main-date-picker
              v-model="form.startDate"
              type="datetime"
              :label="$tc('tournament.registration_start_date')"
              :validator="$v.form.startDate"
            />
          </el-col>
          <el-col :span="12">
            <main-date-picker
              v-model="form.endDate"
              type="datetime"
              :label="$tc('tournament.registration_end_date')"
              :validator="$v.form.endDate"
            />
          </el-col>
        </el-row>
      </panel>

      <panel :title="$tc('tournament.files_and_images')">
        <main-upload-input
          v-model="cover"
          :label="$tc('general.cover_image')"
        />
        <main-upload-input
          v-model="images"
          :label="$tc('general.images')"
          multiple
        />
        <main-upload-input
          v-model="documents"
          :label="$tc('general.documents')"
          multiple
        />
      </panel>

      <panel :title="$tc('tournament.certificates')">
        <document-types-panel :seminar-id="seminarId || (seminar && seminar.id)" />
      </panel>

      <panel v-if="!isEdit || $feature.registration_fee" :title="$tc('general.registration_fee')">
        <el-table :data="registrationFeeData" style="width: 100%">
          <el-table-column :label="$tc('form.description')">
            <template slot-scope="scope">{{ scope.row.desc }}</template>
          </el-table-column>
          <el-table-column :label="$tc('form.price')" width="200px">
            <template slot-scope="scope">
              <main-input
                v-model="form[scope.row.key]"
                :validator="$v.form[scope.row.key]"
                :append="$config.currency"
              />
            </template>
          </el-table-column>
          <el-table-column :label="$tc('form.discount')" width="170px" align="right">
            <template slot-scope="scope">
              <main-input
                v-model="form[scope.row.break_kee]"
                :validator="$v.form[scope.row.break_kee]"
                :append="$config.currency"
              />
            </template>
          </el-table-column>
        </el-table>
      </panel>

      <panel>
        <div class="flex-between">
          <el-button @click="onCancel()">{{ $tc('general.cancel') }}</el-button>
          <el-button style="width: 400px" type="primary" @click="save()">
            {{ isEdit ? $tc('general.edit') : $tc('general.create') }}
          </el-button>
        </div>
      </panel>
    </template>
  </div>
</template>

<script>
import errorHandler from '@/common/errorHandler.js'
import { required, integer } from 'vuelidate/lib/validators'
import DocumentTypesPanel from '@/components/documents/DocumentTypesPanel.vue'

export default {
  components: {
    DocumentTypesPanel
  },
  data () {
    return {
      form: {
        name: null,
        description: null,
        startDate: null,
        endDate: null,
        isCamp: null,
        single_fee_1: null,
        single_fee_1_break: null
      },
      registrationFeeData: [
        { desc: this.$tc('general.registration_fee'), key: 'single_fee_1', break_kee: 'single_fee_1_break' }
      ],
      cover: null,
      images: null,
      documents: null,
      error: false,
      loading: false
    }
  },

  computed: {
    isEdit () {
      return this.$route.name === 'EditSeminar'
    },

    seminarId () {
      return this.$route.params.id
    },

    seminar () {
      return this.isEdit ? this.$store.getters['seminars/get'](this.seminarId) : null
    },

    typeOptions () {
      return [
        { id: true, text: this.$tc('seminar.camp') },
        { id: false, text: this.$tc('seminar.seminar') }
      ]
    }
  },

  created () {
    if (this.isEdit) {
      this.load()
    }
    this.setPageData()
  },

  watch: {
    'seminar.id': {
      handler () {
        this.initializeSeminarData()
      },
      immediate: true
    }
  },

  methods: {
    initializeSeminarData () {
      if (this.seminar) {
        this.form = {
          name: this.seminar.name,
          description: this.seminar.description ? this.seminar.description.replaceAll('<br />', '') : '',
          startDate: this.seminar.startDate,
          endDate: this.seminar.endDate,
          isCamp: this.seminar.isCamp || false,
          single_fee_1: this.seminar.single_fee_1,
          single_fee_1_break: this.seminar.single_fee_1_break
        }
      }
    },

    onCancel () {
      if (this.isEdit) {
        return this.$router.push({ name: 'SeminarView', params: { id: this.seminar.id } })
      }
      this.$router.push({ name: 'Seminars' })
    },

    async load () {
      this.loading = true
      try {
        await this.$store.dispatch('seminars/get', this.seminarId)
        this.setPageData()
        this.error = false
      } catch (e) {
        console.error(e)
        this.error = true
      } finally {
        this.loading = false
      }
    },

    setPageData () {
      const breadcrumbs = [
        { text: this.$tc('seminar.seminars_and_camps'), to: { name: 'Seminars' } },
        ...(this.isEdit ? [{ text: this.seminar?.name, to: { name: 'SeminarView', params: { id: this.seminar?.id } } }] : []),
        { text: this.isEdit ? this.$tc('general.edit') : this.$tc('seminar.create_seminar') }
      ]
      this.$store.commit('app/set', {
        title: this.isEdit ? (this.seminar?.name ?? this.$tc('general.edit')) : this.$tc('seminar.create_seminar'),
        breadcrumbs
      })
    },

    async save () {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) return

      this.loading = true
      try {
        let seminar = this.seminar
        const payload = {
          name: this.form.name,
          description: this.form.description,
          startDate: this.form.startDate,
          endDate: this.form.endDate || undefined,
          isCamp: this.form.isCamp === true,
          single_fee_1: this.form.single_fee_1,
          single_fee_1_break: this.form.single_fee_1_break
        }

        if (this.isEdit) {
          seminar = await this.$store.dispatch('seminars/update', { id: this.seminar.id, payload })
        } else {
          seminar = await this.$store.dispatch('seminars/create', payload)
        }

        if (this.documents?.length) {
          await Promise.all(this.documents.map((document) =>
            this.$store.dispatch('seminarDocuments/create', {
              path: document,
              seminarId: seminar.id,
              name: document.name,
              size: document.size
            }).then((obj) =>
              this.$store.dispatch('seminarDocuments/upload', { id: obj.id, file: document })
            )
          ))
        }

        if (this.images?.length) {
          await Promise.all(this.images.map((image) =>
            this.$store.dispatch('seminarImages/create', {
              path: image,
              seminarId: seminar.id,
              name: image.name,
              size: image.size
            }).then((obj) =>
              this.$store.dispatch('seminarImages/upload', { id: obj.id, file: image })
            )
          ))
        }

        if (this.cover) {
          seminar = await this.$store.dispatch('seminars/upload', { id: seminar.id, file: this.cover })
        }

        this.$message.success(this.isEdit ? this.$tc('messages.success_updated') : this.$tc('messages.success_created'))
        await this.$store.dispatch('app/loadData')
        this.$router.push({ name: 'SeminarView', params: { id: seminar.id } })
      } catch (error) {
        console.error(error)
        errorHandler(error)
      } finally {
        this.loading = false
      }
    }
  },

  validations () {
    return {
      form: {
        name: { required },
        description: { required },
        startDate: { required },
        single_fee_1: { required, integer },
        single_fee_1_break: { required, integer }
      }
    }
  }
}
</script>

<style scoped>
.name-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}
.name-row > *:first-child {
  margin-top: 15px;
}
.type-select {
  width: 150px;
  flex-shrink: 0;
}
.name-row > *:last-child {
  flex: 1;
  min-width: 0;
}
</style>
